/** 
 * @namespace LoginRegistration
 * @memberof Promega.Shell
 * @description Methods for controlling login/logout/create an account Ajax forms on the page.
 */

import { login, logout, register, forgotPassword, resendVerification, resetPassword } from './auth.js';
import {
    lockMenu as lockNavMenu,
    unlockMenu as unlockNavMenu,
    hideActiveMenu as hideActiveNavMenu,
    getActiveMenu as getActiveNavMenu,
    showMenu as showNavMenu,
    isMenuOpen as isNavMenuOpen
} from './navMenu.js';
import { show as showModal, hide as hideModal, lock as lockModal, unlock as unlockModal, isVisible as isModalVisible } from '../components/modal.js';
import smoothScrollIntoView from '../util/smoothScrollIntoView.js';
import { v4 as uuid } from 'uuid';
import closest from '../util/closest.js';
import getQueryStringParameter from '../util/getQueryStringParameter.js';
import removeQueryStringParameter from '../util/removeQueryStringParameter.js';
import addOrReplaceQueryStringParameter from '../util/addOrReplaceQueryStringParameter.js';
import getTestEnvironment from '../util/getTestEnvironment.js';

if (typeof window !== "undefined") {
    window.captchaCallback = captchaCallback;
    window.captchaExpired = captchaExpired;
}
let captchaToken = null;

const LOGIN_FIELD_CLASS = "site-header__login-field",
    REGISTER_FIELD_CLASS = "site-header__register-field",
    LOGIN_LOADING_CLASS = "site-header__login-loading-indicator",
    REGISTER_LOADING_CLASS = "site-header__register-loading-indicator",
    FORGOT_LOADING_CLASS = "site-header__forgot-password-loading-indicator",
    FORGOT_PASSWORD_CLASS = "site-header__forgot-password-field",
    ERROR_MESSAGE_CLASS = "site-header__login-logout-message",
    REGISTER_ERROR_MESSAGE_CLASS = "site-header__reg-message";

const registrationModels = {};

let forgotPasswordQuery;
let usernameForPasswordReset = null;

let forgotUsername, forgotSubmit, forgotLoading, forgotFields;

let completedRegistration = false,
    completedPasswordChange = false,
    completedForgotPasswordModal = false,
    completedResendVerificationEmail = false;

const LOGIN_MAX_ATTEMPTS = 6;
const ACCOUNT_PAGE_LOCATION = "/commerce/account/default";

let _loginSuccessRedirect = null;
export const settings = {
    get loginSuccessRedirect() {
        return _loginSuccessRedirect;
    },

    set loginSuccessRedirect(url) {
        _loginSuccessRedirect = url;
    }
}

function captchaCallback(token) {
    captchaToken = token;
}

function captchaExpired() {
    captchaToken = null;
}

function getLoginUsernameInput(parentEl) {
    return parentEl.querySelector(`.${LOGIN_FIELD_CLASS}[name="username"]`);
}

function getLoginPasswordInput(parentEl) {
    return parentEl.querySelector(`.${LOGIN_FIELD_CLASS}[name="password"]`);
}

function diffInSeconds(end, start) {
    return `${Math.floor((end - start) / 1000)} seconds`
}

/**
 * @param {HTMLElement} parentEl
 * @returns {HTMLElement}
 * @private
 * */
function getLoginSubmitButton(parentEl) {
    return parentEl.querySelector(`.${LOGIN_FIELD_CLASS}[name="submit-modal"]`);
}

/**
 * Disables all login and registration triggers and input fields in all instances of this component.
 * @private
 * */
function disableLoginFields() {
    // Disable all fields
    const allInputs = Array.from(document.querySelectorAll(`.${LOGIN_FIELD_CLASS}`))
        .concat(Array.from(document.querySelectorAll(`.${REGISTER_FIELD_CLASS}`)))
        .concat(Array.from(document.querySelectorAll('.trigger--login')))
        .concat(Array.from(document.querySelectorAll('.trigger--create-account-dialog')))
        .concat(Array.from(document.querySelectorAll('.trigger--forgot-password-dialog')))
        .concat(Array.from(document.querySelectorAll('.site-header__password-reset-field')))
        .concat(Array.from(document.querySelectorAll('.site-header__resend-verification-button')));

    allInputs.forEach(currentInput => currentInput.setAttribute("disabled", "disabled"));
}

/**
 * Enables all login and registration triggers and input fields in all instances of this component.
 * @private
 * */
function enableLoginFields() {
    const allInputs = Array.from(document.querySelectorAll(`.${LOGIN_FIELD_CLASS}`))
        .concat(Array.from(document.querySelectorAll(`.${REGISTER_FIELD_CLASS}`)))
        .concat(Array.from(document.querySelectorAll('.trigger--login')))
        .concat(Array.from(document.querySelectorAll('.trigger--create-account-dialog')))
        .concat(Array.from(document.querySelectorAll('.trigger--forgot-password-dialog')))
        .concat(Array.from(document.querySelectorAll('.site-header__password-reset-field')))
        .concat(Array.from(document.querySelectorAll('.site-header__resend-verification-button')));

    allInputs.forEach(currentInput => currentInput.removeAttribute("disabled"));
}

/**
 * Clicked the "Log In" button in a login/registration component.
 * @param {HTMLElement} parentEl
 * @private
 */
function navRequestedLogin(parentEl) {
    const loginInput = getLoginUsernameInput(parentEl);
    const passwordInput = getLoginPasswordInput(parentEl);
    const loginSubmitButton = getLoginSubmitButton(parentEl);
    let valid = true;
    if (loginInput.value.length === 0) {
        valid = false;
        const errorMessage = parentEl.querySelector(`.${ERROR_MESSAGE_CLASS}[data-message-type="usernameRequired"]`)
        errorMessage.removeAttribute("hidden");
        loginInput.setCustomValidity(errorMessage.innerText);
        loginInput.classList.add("touched");
    }
    if (passwordInput.value.length === 0) {
        valid = false;
        const errorMessage = parentEl.querySelector(`.${ERROR_MESSAGE_CLASS}[data-message-type="passwordRequired"]`);
        errorMessage.removeAttribute("hidden");
        passwordInput.setCustomValidity(errorMessage.innerText);
        passwordInput.classList.add("touched");
    }

    if (valid) {
        lockNavMenu();
        disableLoginFields();
        parentEl.querySelector(`.${LOGIN_LOADING_CLASS}`).classList.add("loading-indicator--visible");
        loginSubmitButton.setAttribute("hidden", "hidden");
        Array.from(document.querySelectorAll(`.${ERROR_MESSAGE_CLASS}`)).forEach(current => {
            current.setAttribute("hidden", "hidden");
        });

        const startTime = Date.now();
        login(getLoginUsernameInput(parentEl).value, getLoginPasswordInput(parentEl).value).then(() => {
            if (typeof window.trackEvent === "function") {
                trackEvent("Shell", " Perf - Login success", diffInSeconds(Date.now(), startTime))
            }

            const pageUrl = new URL(window.location.href);
            const searchParams = new URLSearchParams(pageUrl.search);

            let currentPathname = window.location.pathname.toLowerCase();
            if (currentPathname[currentPathname.length - 1] !== "/") currentPathname += "/";

            const sitecoreToggle = document.querySelector("#sitecore-username-toggle");
            const displayEmailMessaging = sitecoreToggle
                ? sitecoreToggle.getAttribute("data-display-email-username").toLowerCase() === "true"
                : false;

            if (displayEmailMessaging) {
                const isValidEmail = (email) => {
                    var regex = /^[\w!#$%&'*+/=?`{|}~^-]+(?:\.[\w!#$%&'*+/=?`{|}~^-]+)*@(?:[A-Z0-9-]+\.)+[A-Z]{2,}$/i;
                    return regex.test(email);
                }
                const usernameIsEmail = isValidEmail(getLoginUsernameInput(parentEl).value);

                if (!usernameIsEmail) {
                    if (!searchParams.has("isEmail")) {
                        searchParams.append("isEmail", usernameIsEmail);
                        pageUrl.search = searchParams.toString(); // Update the search part of the URL
                        // Assuming you want to navigate relative to the current origin
                        _loginSuccessRedirect = `${ACCOUNT_PAGE_LOCATION}/profile?${searchParams}`;
                    }
                }
            }

            if (typeof _loginSuccessRedirect === "string") {
                window.location = `https://${getTestEnvironment() || "www"}.promega.com${_loginSuccessRedirect}`;
            }
            else if (currentPathname.endsWith("login/")) {
                // Determine if there is a returnUrl to go to
                let returnUrl = decodeURIComponent(getQueryStringParameter(location.href.toLowerCase(), "returnurl"));
                if (returnUrl.length > 0) {
                    window.location = returnUrl;
                } else {
                    window.location = ACCOUNT_PAGE_LOCATION;
                }
            }
            else {
                
                if (!searchParams.has("loginAttempt")) {
                    searchParams.append("loginAttempt", "true");
                }
                window.location = `https://${getTestEnvironment() || "www"}.promega.com${location.pathname}?${searchParams.toString()}${location.hash}`;
            }
            
        }).catch(errorData => {
            if (typeof window.trackEvent === "function") {
                trackEvent("Shell", "Perf - Login failure", diffInSeconds(Date.now(), startTime))
            }
            if (errorData) {
                let errorMessageContainer = parentEl.querySelector(`.${ERROR_MESSAGE_CLASS}[data-message-type="${errorData.ErrorType}"]`)
                if (errorMessageContainer) {
                    errorMessageContainer.removeAttribute("hidden");
                    const usernameInput = getLoginUsernameInput(parentEl);
                    const passwordInput = getLoginPasswordInput(parentEl);
                    let updateUsernameValidity = false, updatePasswordValidity = false;

                    if (errorData.ErrorType === "AccountNotFound") {
                        updateUsernameValidity = true;
                    }
                    if (errorData.ErrorType === "InvalidPassword") {
                        updatePasswordValidity = true;

                        const attempts = parseInt(errorData.Message, 10);
                        if (attempts > 1) {
                            // Reset "attempts remaining" translations
                            const variations = document.querySelectorAll(".site-header__login-attempts-remaining");
                            for (let i = 0; i < variations.length; i++) {
                                variations[i].setAttribute("hidden", "hidden");
                            }

                            // Show the current translated variation
                            const currentVariation = document.querySelector(`.site-header__login-attempts-remaining[data-attempts-remaining="${LOGIN_MAX_ATTEMPTS - attempts}"]`)
                            if (currentVariation)
                                currentVariation.removeAttribute("hidden");

                            // Show the modal
                            showModal(document.querySelector(".site-header__account-lock-warning-modal"));
                        }
                    }
                    if (errorData.ErrorType === "AccountLocked" || errorData.ErrorType === "AccountInactive") {
                        updateUsernameValidity = true;
                        showModal(document.querySelector(".site-header__account-locked-modal"));
                    }
                    if (errorData.ErrorType === "PasswordChangeRequired") {
                        updatePasswordValidity = true;
                        showPasswordResetModal(usernameInput.value);
                    }
                    if (errorData.ErrorType === "AccountNotVerified") {
                        updateUsernameValidity = true;
                        const unverifiedEmailModal = document.querySelector(".site-header__unverified-email-modal");
                        unverifiedEmailModal.setAttribute("data-email-to-verify", getLoginUsernameInput(parentEl).value);
                        completedResendVerificationEmail = false;
                        showModal(unverifiedEmailModal, null, {
                            afterHide: () => {
                                if (typeof window.trackEvent === "function") {
                                    trackEvent("Shell", "Behavior - Resend account verification email", completedResendVerificationEmail ? "Completed" : "Abandoned");
                                }
                            }
                        });
                    }

                    if (updateUsernameValidity) {
                        usernameInput.setCustomValidity(errorMessageContainer.innerText);
                        usernameInput.classList.add("touched");
                    }
                    if (updatePasswordValidity) {
                        passwordInput.setCustomValidity(errorMessageContainer.innerText);
                        passwordInput.classList.add("touched");
                    }
                }
                else {
                    // Show generic error modal
                    showModal(document.querySelector(".site-header__login-generic-error-modal"));
                    const errorMessageContainer = parentEl.querySelector(`.${ERROR_MESSAGE_CLASS}:not([data-message-type])`);
                    errorMessageContainer.removeAttribute("hidden");
                }
            }
            else {
                // Most likely some kind of network error
                showModal(document.querySelector(".site-header__network-error-modal"));
                const errorMessageContainer = parentEl.querySelector(`.${ERROR_MESSAGE_CLASS}:not([data-message-type])`);
                errorMessageContainer.removeAttribute("hidden");
            }

            enableLoginFields();
            parentEl.querySelector(`.${LOGIN_LOADING_CLASS}`).classList.remove("loading-indicator--visible");
            loginSubmitButton.removeAttribute("hidden");
        });
    }
}

/**
 * Clicked the "Create Account" button in a login/registration component.
 * @param {HTMLElement} parentEl
 * @private
 */
function navRequestedRegistration(parentEl) {
    Array.from(parentEl.querySelectorAll(`.${REGISTER_FIELD_CLASS}`)).forEach(currentEl => currentEl.classList.add("touched"));
    if (validateRegistrationModel(parentEl, registrationModels[parentEl.getAttribute("data-registration-component-id")])) {
        const submitButton = parentEl.querySelector(".site-header__register-field[name=submit-modal]");
        lockModal();
        disableLoginFields();
        parentEl.querySelector(`.${REGISTER_LOADING_CLASS}`).classList.add("loading-indicator--visible");
        submitButton.setAttribute("hidden", "hidden");

        const registrationModel = registrationModels[parentEl.getAttribute("data-registration-component-id")];

        // Prepare submission data
        const newData = Object.assign({}, registrationModel, { topyenoh: captchaToken});
        delete newData.confirmPassword;

        const startTime = Date.now();
        register(newData).then(() => {
            if (typeof window.trackEvent === "function") {
                trackEvent("Shell", "Perf - Registration success", diffInSeconds(Date.now(), startTime))
            }

            unlockModal();
            enableLoginFields();
            completedRegistration = true;

            function confirmRegAfterhide() {
                if (document.querySelector("main .registration-component"))
                    window.location.href = "/";
            }

            if (isModalVisible())
                hideModal().then(showModal.bind(null, document.querySelector(".site-header__confirm-registration-modal"), null, { afterHide: confirmRegAfterhide }));
            else
                showModal(document.querySelector(".site-header__confirm-registration-modal"), null, { afterHide: confirmRegAfterhide });
            
            parentEl.querySelector(`.${REGISTER_LOADING_CLASS}`).classList.remove("loading-indicator--visible");
            submitButton.removeAttribute("hidden");
        }).catch(errorMessageObject => {
            unlockModal();
            enableLoginFields();

            if (errorMessageObject) {
                if (typeof window.trackEvent === "function") {
                    trackEvent("Shell", "Perf - Registration failure", diffInSeconds(Date.now(), startTime))
                }

                let errorMessageContainer = parentEl.querySelector(`.${REGISTER_ERROR_MESSAGE_CLASS}[data-message-type="${errorMessageObject.Message}"]`)
                if (errorMessageContainer) {
                    errorMessageContainer.removeAttribute("hidden");
                    if (errorMessageObject.Message === "UsernameUnavailable") {
                        const inputEl = parentEl.querySelector(`.${REGISTER_FIELD_CLASS}[name=username]`);
                        inputEl.setCustomValidity(errorMessageContainer.innerText);

                        showModal(document.querySelector(".site-header__registration-existing-account-modal"));
                    }
                }
                else {
                    // Show generic error modal
                    showModal(document.querySelector(".site-header__registration-generic-error-modal"));
                    errorMessageContainer = parentEl.querySelector(`.${REGISTER_ERROR_MESSAGE_CLASS}:not([data-message-type])`);
                    errorMessageContainer.removeAttribute("hidden");
                }
            }
            else {
                // Most likely some kind of network error
                showModal(document.querySelector(".site-header__network-error-modal"));
                const errorMessageContainer = parentEl.querySelector(`.${REGISTER_ERROR_MESSAGE_CLASS}:not([data-message-type])`);
                errorMessageContainer.removeAttribute("hidden");
            }
            
            parentEl.querySelector(`.${REGISTER_LOADING_CLASS}`).classList.remove("loading-indicator--visible");
            submitButton.removeAttribute("hidden");

            // Reset password fields
            resetRegistrationPasswordFields(parentEl);
        });
    }
    else {
        // Todo: display UI with details about the error and/or a possible resolution
    }
}

/**
 * Submitted a "forgot password" reset request in a login/registration component.
 * @private
 */
function navRequestedPasswordReset() {
    if (forgotUsername && forgotSubmit) {
        // Reset validation messages
        resetForgotPasswordValidity();

        if (forgotUsername.value.length > 0) {
            // Disable field and button
            forgotFields.forEach(current => current.setAttribute("disabled", "disabled"));

            // Set loading state
            forgotLoading.classList.add("loading-indicator--visible");
            forgotSubmit.setAttribute("hidden", "hidden");
            lockModal();

            const startTime = Date.now();
            forgotPassword(forgotUsername.value).then(maskedEmail => {
                if (typeof window.trackEvent === "function") {
                    trackEvent("Shell", "Perf - Request password reset success", diffInSeconds(Date.now(), startTime))
                }

                completedForgotPasswordModal = true;
                unlockModal();
                hideModal().then(() => showModal(document.querySelector(".site-header__forgot-password-email-sent-modal")));
                resetPasswordResetFields();
            }).catch(error => {
                if (typeof window.trackEvent === "function") {
                    trackEvent("Shell", "Perf - Request password reset failure", diffInSeconds(Date.now(), startTime))
                }

                resetPasswordResetFields();
                unlockModal();

                if (error) {
                    if (error.ErrorType === "AccountNotFound") {
                        const errMsgEl = document.querySelector(`.site-header__forgot-password-message[name=${error.ErrorType}]`);
                        errMsgEl.removeAttribute("hidden");
                        forgotUsername.setCustomValidity(errMsgEl.value);
                        forgotUsername.classList.add("touched");
                    }
                    else {
                        const errorModal = document.querySelector(".site-header__forgot-password-error-modal");
                        if (isModalVisible())
                            hideModal().then(() => showModal(errorModal));
                        else
                            showModal(errorModal);
                    }
                }
                else {
                    // Most likely some kind of network error
                    const networkErrorModal = document.querySelector(".site-header__network-error-modal");
                    if (isModalVisible())
                        hideModal().then(() => showModal(networkErrorModal));
                    else
                        showModal(networkErrorModal);
                }
            });
        }
        else {
            const errMsgEl = document.querySelector(`.site-header__forgot-password-message[name=usernameRequired`);
            errMsgEl.removeAttribute("hidden");
            forgotUsername.setCustomValidity(errMsgEl.innerText)
        }
    }
}

/**
 * Submitted a "resend email verification" request in a login/registration component.
 * @param {HTMLElement} parentEl
 * @private
 */
function navRequestedResendEmailVerification(event) {
    disableLoginFields();

    const loadingIndicator = document.querySelector(".site-header__resend-verification-loading-indicator");
    loadingIndicator.classList.add("loading-indicator--visible");
    event.target.setAttribute("hidden", "hidden");

    const parentEl = closest(event.target, ".modal-dialog");
    lockModal();

    const startTime = Date.now();
    resendVerification(parentEl.getAttribute("data-email-to-verify")).then(() => {
        // Success
        if (typeof window.trackEvent === "function") {
            trackEvent("Shell", "Perf - Resend account verification email success", diffInSeconds(Date.now(), startTime))
        }

        enableLoginFields();
        unlockModal();
        loadingIndicator.classList.remove("loading-indicator--visible");
        event.target.removeAttribute("hidden");
        parentEl.removeAttribute("data-email-to-verify");
        completedResendVerificationEmail = true;
        hideModal().then(() => showModal(document.querySelector(".site-header__unverified-email-success-modal")));
    }).catch(errorMessage => {
        if (typeof window.trackEvent === "function") {
            trackEvent("Shell", "Perf - Resend account verification email failure", diffInSeconds(Date.now(), startTime))
        }


        enableLoginFields();
        unlockModal();
        loadingIndicator.classList.remove("loading-indicator--visible");
        event.target.removeAttribute("hidden");

        if (errorMessage) {
            // Unable to send verification email. Show "contact customer service" message.
            parentEl.removeAttribute("data-email-to-verify");
            hideModal().then(() => showModal(document.querySelector(".site-header__unverified-email-error-modal")));
        }
        else {
            // Most likely some kind of network error
            const networkErrorModal = document.querySelector(".site-header__network-error-modal");
            if (isModalVisible())
                hideModal().then(() => showModal(networkErrorModal));
            else
                showModal(networkErrorModal);
        }
    });
}

function navRequestedLogout(event) {
    const logoutButton = event.target;
    const logoutLoading = document.querySelector(".site-header__logout-loading-indicator");

    lockNavMenu();
    logoutButton.setAttribute("hidden", "hidden");
    logoutLoading.classList.add("loading-indicator--visible");

    const startTime = Date.now();
    logout().then((result) => {
        if (typeof window.trackEvent === "function") {
            trackEvent("Shell", "Perf - Logout success", diffInSeconds(Date.now(), startTime))
        }

        let destinationUrl = `https://${getTestEnvironment() || "www"}.promega.com${location.pathname}${location.search}${location.hash}`;
        if (window.location.pathname.toLowerCase().startsWith("/commerce")) {
            destinationUrl = `https://${getTestEnvironment() || "www"}.promega.com`;
        }
        
        location.href = addOrReplaceQueryStringParameter(destinationUrl, "showmessage", "logged-out");
    }).catch((error) => {
        if (typeof window.trackEvent === "function") {
            trackEvent("Shell", "Perf - Logout failure", diffInSeconds(Date.now(), startTime))
        }

        logoutButton.removeAttribute("hidden");
        logoutLoading.classList.remove("loading-indicator--visible");

        if (!error) {
            // Most likely some kind of network error
            const networkErrorModal = document.querySelector(".site-header__network-error-modal");
            if (isModalVisible())
                hideModal().then(() => showModal(networkErrorModal));
            else
                showModal(networkErrorModal);
        }

        // Todo: display UI with details about the error and/or a possible resolution
    });
}

function navRequestedSetNewPassword(event) {
    const oldPassword = document.querySelector(".site-header__password-reset-field[name=oldPassword]");
    const password = document.querySelector(".site-header__password-reset-field[name=password]");
    const confirmPassword = document.querySelector(".site-header__password-reset-field[name=confirmPassword]");
    const submitButton = document.querySelector(".site-header__password-reset-field[name=submit-modal]");

    password.classList.add("touched");
    confirmPassword.classList.add("touched");

    if (validatePasswordResetFields()) {
        disableLoginFields();

        const loadingIndicator = document.querySelector(".site-header__password-reset-loading-indicator");
        loadingIndicator.classList.add("loading-indicator--visible");
        submitButton.setAttribute("hidden", "hidden");
        lockModal();

        const resetPasswordConfig = {
            password: password.value
        };
        if (typeof usernameForPasswordReset === "string") {
            resetPasswordConfig.oldPassword = oldPassword.value;
            resetPasswordConfig.username = usernameForPasswordReset;
        }
        else
            resetPasswordConfig.loginRpId = forgotPasswordQuery;

        const startTime = Date.now();
        resetPassword(resetPasswordConfig).then(result => {
            // Success
            if (typeof window.trackEvent === "function") {
                trackEvent("Shell", "Perf - Set new account password success", diffInSeconds(Date.now(), startTime))
            }

            enableLoginFields();
            loadingIndicator.classList.remove("loading-indicator--visible");
            submitButton.removeAttribute("hidden");
            unlockModal();
            completedPasswordChange = true;
            const passwordFields = Array.from(document.querySelectorAll(".site-header__login-field[name=password]"));
            passwordFields.forEach(current => resetLoginFieldValidation({ target: current })); 
            hideModal().then(() => showModal(document.querySelector(".site-header__password-reset-success-modal")));
        }).catch(error => {
            // Error
            if (typeof window.trackEvent === "function") {
                trackEvent("Shell", "Perf - Set new account password failure", diffInSeconds(Date.now(), startTime))
            }

            enableLoginFields();
            unlockModal();
            loadingIndicator.classList.remove("loading-indicator--visible");
            submitButton.removeAttribute("hidden");
            if (error) {
                // Update UI based on the error type
                const errorMessageContainer = document.querySelector(`.site-header__password-reset-message[data-message-type="${error.ErrorType}"]`);
                if (errorMessageContainer) {
                    errorMessageContainer.removeAttribute("hidden");

                    if (error.ErrorType === "PasswordUsedRecently") {
                        const inputEl = document.querySelector(".site-header__password-reset-field[name=password]");
                        inputEl.setCustomValidity(errorMessageContainer.innerText);
                    }
                }
                else if (error.Message === "PasswordResetLinkExpired") {
                    hideModal().then(() => showModal(document.querySelector(".site-header__password-reset-link-expired")));
                }
                else {
                    // Other error. Show generic password reset error dialog.
                    hideModal().then(() => showModal(document.querySelector(".site-header__password-reset-error-modal")));
                }
            }
            else {
                // Most likely some kind of network error
                const networkErrorModal = document.querySelector(".site-header__network-error-modal");
                if (isModalVisible())
                    hideModal().then(() => showModal(networkErrorModal));
                else
                    showModal(networkErrorModal);
            }
        });
    }
}

/**
 * Reset all "forgot password" fields for the password reset functionality in a login/registration component.
 * @private
 */
function resetPasswordResetFields() {
    forgotFields.forEach(current => current.removeAttribute("disabled"));
    forgotLoading.classList.remove("loading-indicator--visible");
    forgotSubmit.removeAttribute("hidden");
}

/**
 * Reset all registration fields in a login/registration component.
 * @param {HTMLElement} parentEl
 * @private
 */
function resetRegistrationFields(parentEl) {
    const registrationFields = parentEl.querySelectorAll(`.${REGISTER_FIELD_CLASS}`);
    for (let i = 0; i < registrationFields.length; i++) {
        if (registrationFields[i].name !== "country" && registrationFields[i].name !== "language") {
            registrationFields[i].value = "";
        }
            
        registrationFields[i].classList.remove("touched");
        registrationFields[i].setCustomValidity("");
    }

    const loadingIndicators = parentEl.querySelectorAll(`.${REGISTER_LOADING_CLASS}`);
    for (let i = 0; i < loadingIndicators.length; i++) {
        loadingIndicators[i].classList.remove("loading-indicator--visible");
    }
}

function resetRegistrationPasswordFields(parentEl) {
    const passwordFields = parentEl.querySelectorAll(`.${REGISTER_FIELD_CLASS}[name=password], .${REGISTER_FIELD_CLASS}[name=confirmPassword]`),
        modelId = parentEl.getAttribute("data-registration-component-id");
    for (let i = 0; i < passwordFields.length; i++) {
        passwordFields[i].value = "";
        passwordFields[i].classList.remove("touched");
        passwordFields[i].setCustomValidity("");
        registrationModels[modelId][passwordFields[i].name] = "";
    }
}

/**
 * Hide all registration validation messages in a login/registration.
 * @param {HTMLElement} parentEl
 * @private
 */
function resetRegistrationValidationMessages(parentEl) {
    Array.prototype.slice.call(parentEl.querySelectorAll(`.${REGISTER_ERROR_MESSAGE_CLASS}`)).forEach(current => current.setAttribute("hidden", "hidden"));
}

/**
 * Resets the registration model connected to the given registration component.
 * @param {HTMLElement} parentEl
 * @private
 */
function resetRegistrationModel(parentEl) {
    const modelId = parentEl.getAttribute("data-registration-component-id");
    Object.keys(registrationModels[modelId]).forEach(currentKey => {
        // Temporary: keep the "country" and "language" fields since there's not a user input for it yet
        if (currentKey !== "country" && currentKey !== "language")
            registrationModels[modelId][currentKey] = "";
    });
}

function validateRegistrationModel(parentEl, registrationModel, options) {
    let valid = true;

    // Reset all validation messages
    resetRegistrationValidationMessages(parentEl);

    // Make sure all fields have data
    Object.keys(registrationModel).forEach(current => {
        const inputEl = parentEl.querySelector(`.${REGISTER_FIELD_CLASS}[name="${current}"]`);
        inputEl.setCustomValidity("");

        if (registrationModel[current].length === 0) {
            valid = false;
            const errorMessage = parentEl.querySelector(`.${REGISTER_ERROR_MESSAGE_CLASS}[data-message-type="${current}Required"]`);
            if (inputEl.classList.contains("touched") || (options && options["force"] === current)) {
                errorMessage.removeAttribute("hidden");
                inputEl.setCustomValidity(errorMessage.innerText);
            }
        }
    });

    // Make sure password field passes the pattern regex
    let passwordValidityMessage = null;
    {
        const inputEl = parentEl.querySelector(`.${REGISTER_FIELD_CLASS}[name="password"]`);
        if (!registrationModel.password.match(new RegExp(inputEl.getAttribute("pattern")))) {
            const errorMessage = parentEl.querySelector(`.${REGISTER_ERROR_MESSAGE_CLASS}[data-message-type="PasswordComplexityFail"]`);
            if (inputEl.classList.contains("touched") || (options && options["force"] === "password")) {
                errorMessage.removeAttribute("hidden");
                inputEl.setCustomValidity(errorMessage.innerText);
                passwordValidityMessage = errorMessage.innerText;
            }
        }
    }

    // Make sure password fields match
    if (registrationModel.password !== registrationModel.confirmPassword && !passwordValidityMessage) {
        valid = false;
        const errorMessage = parentEl.querySelector(`.${REGISTER_ERROR_MESSAGE_CLASS}[data-message-type="confirmPasswordNoMatch"]`);
        const inputEl = parentEl.querySelector(`.${REGISTER_FIELD_CLASS}[name="confirmPassword"]`);
        if (inputEl.classList.contains("touched") || (options && options["force"] === "confirmPassword")) {
            errorMessage.removeAttribute("hidden");
            inputEl.setCustomValidity(errorMessage.innerText);
        }
    }
    else if (passwordValidityMessage) {
        // Password is not valid. Mark the confirmation as invalid, too
        valid = false;
        const inputEl = parentEl.querySelector(`.${REGISTER_FIELD_CLASS}[name="confirmPassword"]`);
        if (inputEl.classList.contains("touched") || (options && options["force"] === "confirmPassword")) {
            inputEl.setCustomValidity(passwordValidityMessage);
        }
    }

    // Make sure email/username field is valid
    {
        const inputEl = parentEl.querySelector(`.${REGISTER_FIELD_CLASS}[name="username"]`);
        if (!registrationModel.username.toLowerCase().match(new RegExp(inputEl.getAttribute("data-pattern")))) {
            valid = false;
            const errorMessage = parentEl.querySelector(`.${REGISTER_ERROR_MESSAGE_CLASS}[data-message-type="UsernameNotValidEmail"]`);
            const inputEl = parentEl.querySelector(`.${REGISTER_FIELD_CLASS}[name="username"]`);
            if (inputEl.classList.contains("touched") || (options && options["force"] === "username")) {
                errorMessage.removeAttribute("hidden");
                inputEl.setCustomValidity(errorMessage.innerText);
            }
        }
    }

    return valid;
}

function validatePasswordResetFields(options) {
    let valid = true;

    const oldPassword = document.querySelector(".site-header__password-reset-field[name=oldPassword]");
    const password = document.querySelector(".site-header__password-reset-field[name=password]");
    const confirmPassword = document.querySelector(".site-header__password-reset-field[name=confirmPassword]");

    Array.from(document.querySelectorAll(".site-header__password-reset-message")).forEach(currentEl => currentEl.setAttribute("hidden", "hidden"));

    const currentFields = [password, confirmPassword];
    if (usernameForPasswordReset)
        currentFields.push(oldPassword);

    // Make sure each field has text entered into it
    currentFields.forEach(currentEl => {
        if (currentEl.value.length === 0) {
            valid = false;
            const errorMessage = document.querySelector(`.site-header__password-reset-message[data-message-type="${currentEl.name}Required"]`);
            if (currentEl.classList.contains("touched") || (options && options["force"] === currentEl.name)) {
                errorMessage.removeAttribute("hidden");
                currentEl.setCustomValidity(errorMessage.innerText);
            }
        }
        else currentEl.setCustomValidity("");
    });

    let passwordValidityMessage = null;
    // Make sure password field passes the pattern regex
    {
        if (!password.value.match(new RegExp(password.getAttribute("pattern")))) {
            valid = false;
            const errorMessage = document.querySelector(`.site-header__password-reset-message[data-message-type="PasswordComplexityFail"]`);
            if (password.classList.contains("touched") || (options && options["force"] === "password")) {
                errorMessage.removeAttribute("hidden");
                password.setCustomValidity(errorMessage.innerText);
                passwordValidityMessage = errorMessage.innerText;
            }
        }
    }

    // Make sure password fields match
    if (password.value !== confirmPassword.value && !passwordValidityMessage) {
        valid = false;
        const errorMessage = document.querySelector(`.site-header__password-reset-message[data-message-type="confirmPasswordNoMatch"]`);
        if (confirmPassword.classList.contains("touched") || (options && options["force"] === "confirmPassword")) {
            errorMessage.removeAttribute("hidden");
            confirmPassword.setCustomValidity(errorMessage.innerText);
        }
    }
    else if (passwordValidityMessage) {
        // Password is not valid. Mark the confirmation as invalid, too
        valid = false;
        if (confirmPassword.classList.contains("touched") || (options && options["force"] === "confirmPassword")) {
            confirmPassword.setCustomValidity(passwordValidityMessage);
        }
    }

    return valid;
}

function updateRegistrationModel(event) {
    const parentEl = closest(event.currentTarget, ".registration-component");
    const registrationModel = registrationModels[parentEl.getAttribute("data-registration-component-id")];
    registrationModel[event.target.name] = event.target.value;

    validateRegistrationModel(parentEl, registrationModel);
}

function resetLoginFieldValidation(event) {
    event.target.setCustomValidity("");
    event.target.classList.remove("touched");

    // Hide messages for the current input
    let currentSibling = event.target.parentElement.nextElementSibling;
    while (currentSibling) {
        if (currentSibling.classList.contains("site-header__login-logout-message"))
        currentSibling.setAttribute("hidden", "hidden");
        currentSibling = currentSibling.nextElementSibling;
    }
}

function resetForgotPasswordValidity() {
    Array.from(document.querySelectorAll(".site-header__forgot-password-message")).forEach(currentEl => currentEl.setAttribute("hidden", "hidden"));
    if (forgotUsername) {
        forgotUsername.classList.remove("touched");
        forgotUsername.setCustomValidity("");
    }
}

function showForgotPasswordModalHelper() {
    completedForgotPasswordModal = false;
    showModal(document.querySelector('.site-header__forgot-password-modal'), null, {
        afterHide: () => {
            forgotUsername.value = "";
            forgotUsername.classList.remove("touched");

            if (typeof window.trackEvent === "function") {
                trackEvent("Shell", "Behavior - Request password reset", completedForgotPasswordModal ? "Completed" : "Abandoned");
            }
        }
    });
}

/**
 * @function clearModalLoginFields
 * @memberof Promega.Shell.LoginRegistration
 * @description Clears all the fields in all Login components on the current page, and resets their validation styles.
 */
export function clearModalLoginFields() {
    const modalLoginLoading = document.querySelector(".site-header__flyout--account .login-component .loading-indicator--visible");
    if (!modalLoginLoading) {
        const fieldsToClear = document.querySelectorAll(".site-header__flyout--account .login-component .site-header__login-field");
        for (let i = 0; i < fieldsToClear.length; i++) {
            resetLoginFieldValidation({ target: fieldsToClear[i] });
            fieldsToClear[i].value = "";
        }
    }
}

/**
 * @function clearModalRegistrationFields
 * @memberof Promega.Shell.LoginRegistration
 * @description Clears all the fields in all Registration/Create an Account components on the current page, and resets their validation styles.
 */
export function clearModalRegistrationFields() {
    const regComponent = document.querySelector(".site-header__register-modal .registration-component");
    resetRegistrationFields(regComponent);
    resetRegistrationValidationMessages(regComponent);
    resetRegistrationModel(regComponent);
}

/**
 * @function showPasswordResetModal
 * @memberof Promega.Shell.LoginRegistration
 * @description Shows a modal dialog that allows the user to set a new password.
 * @param {String} [username] A username for the password reset. If not provided, the user will be given a Username field in which to type their username.
 */
export function showPasswordResetModal(username) {
    // Show the password reset dialog

    const oldPasswordContainer = document.querySelector(".site-header__password-reset-old-password-container");
    if (oldPasswordContainer) {
        if (username)
            oldPasswordContainer.hidden = false;
        else
            oldPasswordContainer.hidden = true;
    }

    const resetModal = document.querySelector(".site-header__password-reset-modal");
    if (resetModal) {
        usernameForPasswordReset = username;
        completedPasswordChange = false;

        showModal(resetModal, null, {
            afterHide: () => {
                const passwordResetFields = Array.from(document.querySelectorAll(".site-header__password-reset-field"));
                passwordResetFields.forEach(currentEl => {
                    currentEl.value = "";
                    currentEl.setCustomValidity("");
                    currentEl.classList.remove("touched");
                });

                if (typeof window.trackEvent === "function") {
                    trackEvent("Shell", "Behavior - Set new account password", completedPasswordChange ? "Completed" : "Abandoned");
                }
            }
        }).then(() => {
            lockModal({ allowCloseButton: true });
        });
    }
    else {
        document.addEventListener('DOMContentLoaded', () => {
            showNavMenu('account');
            lockNavMenu();
            const logoutButton = document.querySelector(".site-header__logout-submit-button");
            if (logoutButton) logoutButton.focus();
        });
    }
}


if (typeof document !== "undefined") {
    // Init components
    // Login
    const allLoginComponents = document.querySelectorAll(".login-component");
    for (let i = 0; i < allLoginComponents.length; i++) {
        const currentComponent = allLoginComponents[i];

        const usernameInput = currentComponent.querySelector(`.${LOGIN_FIELD_CLASS}[name="username"]`);
        const passwordInput = currentComponent.querySelector(`.${LOGIN_FIELD_CLASS}[name="password"]`);
        const submitButton = currentComponent.querySelector(`.${LOGIN_FIELD_CLASS}[name="submit-modal"]`);

        if (submitButton) {
            submitButton.addEventListener("click", navRequestedLogin.bind(null, currentComponent));
        }

        if (usernameInput) {
            usernameInput.addEventListener("input", resetLoginFieldValidation);
            usernameInput.addEventListener("keydown", event => event.keyCode === 13 && navRequestedLogin(currentComponent));
        }
        if (passwordInput) {
            passwordInput.addEventListener("input", resetLoginFieldValidation);
            passwordInput.addEventListener("keydown", event => event.keyCode === 13 && navRequestedLogin(currentComponent));
        }
    }

    // Resend verification modal
    const resendVerificationButton = document.querySelector(".site-header__resend-verification-button");
    if (resendVerificationButton) {
        resendVerificationButton.addEventListener("click", navRequestedResendEmailVerification);
    }

    // Forgot password modal
    forgotUsername = document.querySelector(`.${FORGOT_PASSWORD_CLASS}[name=username]`);
    forgotSubmit = document.querySelector(`.${FORGOT_PASSWORD_CLASS}[name=submit-modal]`);
    forgotLoading = document.querySelector(`.${FORGOT_LOADING_CLASS}`);
    forgotFields = Array.from(document.querySelectorAll(`.${FORGOT_PASSWORD_CLASS}`));

    if (forgotUsername && forgotSubmit && forgotLoading) {
        forgotUsername.addEventListener("input", resetForgotPasswordValidity);
        forgotUsername.addEventListener("keydown", event => event.keyCode === 13 && navRequestedPasswordReset());
        forgotSubmit.addEventListener("click", navRequestedPasswordReset);
    }

    // Forgot password triggers
    const allForgotPasswordTriggers = document.querySelectorAll(".trigger--forgot-password-dialog");
    for (let i = 0; i < allForgotPasswordTriggers.length; i++) {
        allForgotPasswordTriggers[i].addEventListener("click", event => {
            event.preventDefault();
            const parentEl = closest(event.target, ".login-component");
            const loginFieldEl = parentEl ? getLoginUsernameInput(parentEl) : null;
            forgotUsername.value = loginFieldEl ? loginFieldEl.value : "";
            document.querySelector(".site-header__forgot-password-message").setAttribute("hidden", "hidden");

            if (isModalVisible()) hideModal().then(showForgotPasswordModalHelper);
            else showForgotPasswordModalHelper();

            document.querySelector(".site-header__forgot-password-field[name=username]").focus();
        });
    }

    // Login triggers
    const allLoginTriggers = document.querySelectorAll(".trigger--login-dialog");
    for (let i = 0; i < allLoginTriggers.length; i++) {
        allLoginTriggers[i].addEventListener("click", event => {
            event.preventDefault();
            if (isModalVisible()) {
                unlockModal();
                hideModal();
            }
            
            smoothScrollIntoView(document.querySelector(".site-header"));

            if (!isNavMenuOpen("account"))
                showNavMenu("account", { autofocus: true });
        });
    }

    // Registration triggers
    const allRegistrationTriggers = document.querySelectorAll(".trigger--create-account-dialog");
    for (let i = 0; i < allRegistrationTriggers.length; i++) {
        allRegistrationTriggers[i].addEventListener("click", event => {
            event.preventDefault();
            if (getActiveNavMenu()) {
                unlockNavMenu();
                hideActiveNavMenu();
            }

            const registerModal = document.querySelector(".site-header__register-modal");
            completedRegistration = false;
            showModal(registerModal, null, {
                width: 480,
                afterHide: () => {
                    clearModalRegistrationFields();
                    if (typeof window.trackEvent === "function") {
                        trackEvent("Shell", "Behavior - Register account", completedRegistration ? "Completed" : "Abandoned");
                    }
                }
            });

            // Focus firstname input in registration modal
            document.querySelector(".site-header__register-modal .site-header__register-field[name=firstname]").focus();
        });
    }

    // Registration
    const allRegistrationComponents = document.querySelectorAll(".registration-component");
    for (let k = 0; k < allRegistrationComponents.length; k++) {
        const componentId = uuid();
        const currentComponent = allRegistrationComponents[k];
        currentComponent.setAttribute("data-registration-component-id", componentId);

        const registrationFields = currentComponent.querySelectorAll(`.${REGISTER_FIELD_CLASS}`);
        const registrationModel = {};
        const boundRequestedRegFn = navRequestedRegistration.bind(null, currentComponent);
        for (let i = 0; i < registrationFields.length; i++) {
            if (registrationFields[i].name === "submit-modal") {
                // Submit button
                registrationFields[i].addEventListener("click", boundRequestedRegFn);
            }
            else if (registrationFields[i].name === "cancel") {
                registrationFields[i].addEventListener("click", () => {
                    hideModal();
                });
            }
            else if (registrationFields[i].name === "name") {
                // Honeypot - do nothing?
            }
            else {
                if (registrationFields[i].name === "page_0$header_0$ctl01$CountriesDropDownList") {
                    registrationFields[i].name = "country";
                }
                registrationModel[registrationFields[i].name] = registrationFields[i].value;
                registrationFields[i].addEventListener("input", updateRegistrationModel);
                registrationFields[i].addEventListener("keydown", event => event.keyCode === 13 && boundRequestedRegFn());
                registrationFields[i].addEventListener("blur", validateRegistrationModel.bind(this, currentComponent, registrationModel, { force: registrationFields[i].name }));
            }
        }
        registrationModels[componentId] = registrationModel;
    }

    // Reset password query string
    forgotPasswordQuery = decodeURIComponent(getQueryStringParameter(location.href, "loginRpId"));
    const passwordResetFields = Array.from(document.querySelectorAll(".site-header__password-reset-field"));
    for (let i = 0; i < passwordResetFields.length; i++) {
        if (passwordResetFields[i].name === "submit-modal") {
            passwordResetFields[i].addEventListener("click", navRequestedSetNewPassword);
        }
        else {
            passwordResetFields[i].addEventListener("input", validatePasswordResetFields);
            passwordResetFields[i].addEventListener("keydown", event => event.keyCode === 13 && navRequestedSetNewPassword());
            passwordResetFields[i].addEventListener("blur", validatePasswordResetFields.bind(null, { force: passwordResetFields[i].name }));
        }
    }

    if (forgotPasswordQuery.length > 0) {
        if (window.history) {
            window.history.replaceState(null, "", removeQueryStringParameter(location.href.toLowerCase(), "loginrpid"));
        }
        showPasswordResetModal(false);
    }
    else {
        const verificationSuccess = getQueryStringParameter(location.href.toLowerCase(), "emailverified");
        if (verificationSuccess.length > 0) {
            window.history.replaceState(null, "", removeQueryStringParameter(location.href.toLowerCase(), "emailverified"));
            if (verificationSuccess === "true") {
                showModal(document.querySelector(".site-header__email-verification-success-modal"), null, {
                    afterHide: () => showNavMenu("account", { autofocus: true })
                });
            }
            else if (verificationSuccess === "china") {
                showModal(document.querySelector(".site-header__email-verification-success-modal"));
            }
            else if (verificationSuccess === "false") {
                showModal(document.querySelector(".site-header__email-verification-error-modal"));
            }
        }
    }

    // Log out
    const logoutButton = document.querySelector(".site-header__logout-submit-button");
    if (logoutButton) logoutButton.addEventListener("click", navRequestedLogout);
}
